/** Public leaderboard, rankable by points, kanji mastered or quiz accuracy. */

import { useEffect, useState } from 'react'
import { ApiError, api, type LeaderRow, type Leaderboard } from '../api'
import { Avatar, EmptyState, ErrorNote, LevelBadge, Spinner } from '../components/ui'

type Metric = 'points' | 'mastered' | 'accuracy'

const METRICS: { value: Metric; label: string }[] = [
  { value: 'points', label: 'Points' },
  { value: 'mastered', label: 'Mastered' },
  { value: 'accuracy', label: 'Accuracy' },
]

/** gold / silver / bronze for the top three ranks */
const MEDALS: Record<number, string> = { 1: '#f59e0b', 2: '#cbd5e1', 3: '#d97706' }

function errText(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Could not reach the server.'
}

function Rank({ rank }: { rank: number }) {
  const medal: string | undefined = MEDALS[rank]
  if (medal === undefined) return <span className="text-sm font-semibold text-ink-500">{rank}</span>
  return (
    <span
      className="inline-flex h-7 w-7 items-center justify-center rounded-full border text-xs font-bold"
      style={{ background: `${medal}22`, color: medal, borderColor: `${medal}88` }}
    >
      {rank}
    </span>
  )
}

function Row({ row, pinned }: { row: LeaderRow; pinned?: boolean }) {
  return (
    <tr
      className={
        row.is_me
          ? 'bg-brand-500/10'
          : pinned
            ? 'bg-ink-850/60'
            : undefined
      }
    >
      <td className="w-14 px-5 py-3">
        <Rank rank={row.rank} />
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-3">
          <Avatar name={row.display_name} seed={row.avatar_seed} size={32} />
          <span className={`truncate font-semibold ${row.is_me ? 'text-brand-500' : 'text-slate-100'}`}>
            {row.display_name}
            {row.is_me && <span className="ml-2 text-[10px] font-bold uppercase tracking-wide">you</span>}
          </span>
        </div>
      </td>
      <td className="px-3 py-3">
        <LevelBadge level={row.target_level} />
      </td>
      <td className="px-3 py-3 text-right text-slate-200">{row.mastered}</td>
      <td className="px-3 py-3 text-right text-slate-200">{Math.round(row.accuracy * 100)}%</td>
      <td className="px-3 py-3 text-right text-ink-500">{row.quizzes}</td>
      <td className="px-5 py-3 text-right font-bold text-slate-100">{row.points.toLocaleString()}</td>
    </tr>
  )
}

export default function Scoreboard() {
  const [metric, setMetric] = useState<Metric>('points')
  const [data, setData] = useState<Leaderboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    void (async () => {
      setLoading(true)
      setError(null)
      try {
        const board = await api.leaderboard(metric)
        if (alive) setData(board)
      } catch (err) {
        if (alive) setError(errText(err))
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [metric])

  // The API returns `me` from the full ranking, which may sit past the cut-off.
  const me = data?.me ?? null
  const meInTable = data ? data.rows.some((r) => r.is_me) : false
  const pinnedMe = me !== null && !meInTable ? me : null

  return (
    <div className="mx-auto w-full max-w-4xl space-y-5 px-5 py-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Scoreboard</h1>
          <p className="text-sm text-ink-500">
            Points are awarded for kanji mastered and correct quiz answers.
          </p>
        </div>
        <div className="flex gap-1 rounded-xl border border-ink-700 bg-ink-950/60 p-1">
          {METRICS.map((m) => (
            <button
              key={m.value}
              type="button"
              aria-pressed={metric === m.value}
              onClick={() => setMetric(m.value)}
              className={`btn px-3 py-1.5 text-xs ${
                metric === m.value ? 'bg-ink-800 text-slate-100' : 'text-ink-500 hover:text-slate-200'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </header>

      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && <Spinner label="Ranking everyone…" />}

      {data && data.rows.length === 0 && !loading && (
        <EmptyState
          title="Nobody on the board yet"
          body="Once learners start mastering kanji and finishing quizzes, they show up here."
        />
      )}

      {data && data.rows.length > 0 && (
        <section className="card overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-ink-700/60 text-left text-[10px] uppercase tracking-wide text-ink-500">
                <th className="px-5 py-2.5 font-semibold">#</th>
                <th className="px-3 py-2.5 font-semibold">Learner</th>
                <th className="px-3 py-2.5 font-semibold">Target</th>
                <th className="px-3 py-2.5 text-right font-semibold">Mastered</th>
                <th className="px-3 py-2.5 text-right font-semibold">Accuracy</th>
                <th className="px-3 py-2.5 text-right font-semibold">Quizzes</th>
                <th className="px-5 py-2.5 text-right font-semibold">Points</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-700/40">
              {data.rows.map((row) => (
                <Row key={row.user_id} row={row} />
              ))}
            </tbody>
            {pinnedMe && (
              <tfoot className="border-t-2 border-ink-700">
                <tr>
                  <td colSpan={7} className="px-5 pt-3 text-[10px] uppercase tracking-wide text-ink-500">
                    Your position
                  </td>
                </tr>
                <Row row={pinnedMe} pinned />
              </tfoot>
            )}
          </table>
        </section>
      )}

      {metric === 'accuracy' && (
        <p className="text-xs text-ink-500">
          Accuracy ranking needs at least 20 answered questions; everyone below that is ordered by points.
        </p>
      )}
    </div>
  )
}
